'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2 } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import type { BulkPayload } from '@/types/bulk-action';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { RECEPTION_HASH } from '../constants';
import { useBulkDeleteShipmentsMutation } from '../services/reception.mutations';
import type { Shipment } from '../types';

interface ReceptionTableActionBarProps {
  table: Table<Shipment>;
  getBulkPayload: () => BulkPayload;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function ReceptionTableActionBar({
  table,
  getBulkPayload,
  onComplete,
  isAllPagesSelected,
  totalCount
}: ReceptionTableActionBarProps) {
  const t = useTranslations('reception');
  const { open: deleteOpen, onOpenChange: toggleDelete } = useHashDialog(
    RECEPTION_HASH.bulkDelete
  );

  const bulkDeleteMutation = useBulkDeleteShipmentsMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeleteMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkDeleteMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    toggleDelete(false);
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleDelete(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </div>
      </div>

      {/* Bulk delete confirmation — #bulk-delete */}
      <AlertDialog open={deleteOpen} onOpenChange={toggleDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
